import { useState } from 'react'

import { useLoading, useModal, useAuth } from 'hooks'
import { createContext } from 'use-context-selector'

import {
  Entity,
  IVisaoIndividual,
  ResponseGetParametrizacaoEfeitvo
} from '../../types'

export const MovMensalContext = createContext<IMovMensalContext | null>(null)

const DEFAULT_PARAMETRIZACAO = {
  dsFimFechamentoAnalista: '',
  dsFimFechamentoFornecedorContraparte: '',
  dsInicioFechamentoAnalista: '',
  dsInicioFechamentoFornecedorContraparte: '',
  flValorHeSalarioBasePorColaborador: false,
  idParametrizacaoEfetivo: 0,
  vlJustVariacaoMaxEfetivo: 0,
  vlJustVariacaoMinEfetivo: 0,
  vlJustVariacaoNumHomemMax: 0,
  vlJustVariacaoNumHomemMin: 0,
  vlVariacaoHorasExtras: 0
} as ResponseGetParametrizacaoEfeitvo

const DEFAULT_MOV_STATE = {
  cdContratoPrincipalId: undefined,
  cdContratoCompartilhado: undefined,
  compAnterior: [],
  compAtual: [],
  contratoId: undefined,
  contratoPrincipalId: undefined,
  flStatusMobilizacao: undefined,
  flValidAnalista: undefined,
  flValidContraparte: undefined,
  fornecedorId: undefined,
  fornecedorPrincipalId: undefined,
  isCompetenciaAtual: undefined,
  movimentacaoId: undefined,
  nmFornecedor: undefined,
  nmFornecedorPrincipal: undefined,
  nuAnoMesCompetencia: undefined,
  nuMesAnoCompetencia: undefined,
  visaoTotal: undefined,
  totalEfetivoComDemitidos: undefined,
  statusMovimentacaoFornecedor: undefined,
  dataConsolidacao: undefined
} as IVisaoIndividual

const DEFAULT_ERROS_FORM = {
  qtHeNormal: null,
  qtHeQtd: null,
  txHeJustificativa: null,
  txSalBaseJustificativa: null,
  vlSalBase: null,
  vlFolhaPagto: null,
  vlHoraExtras: null
} as IErrorsVisionTotal


export interface IMovState {
  movimentacaoId: number | undefined
  contratoId: number | undefined
  cdContratoCompartilhado: string | undefined
  fornecedorId: number | undefined
  contratoPrincipalId: number | undefined
  fornecedorPrincipalId: number | undefined
  nuMesAnoCompetencia: string | undefined
  nmFornecedor: string | undefined
  cdContratoPrincipalId: string | undefined
  nmFornecedorPrincipal: string | undefined
  flStatusMobilizacao: string | undefined
  compAnterior: Entity[] | undefined
  compAtual: Entity[] | undefined
  visaoTotal: IVisaoTotal | undefined
  totalEfetivoComDemitidos: number | undefined
}

type ICheckboxTypes = 'flSemMob' | 'flComMob' | 'flCompartilhado'

export interface IMovMensalContext {
  movState: IVisaoIndividual
  setMovState: React.Dispatch<React.SetStateAction<IVisaoIndividual>>
  setSelectedMovement: React.Dispatch<React.SetStateAction<ISelectedMoviment>>
  parametrization: ResponseGetParametrizacaoEfeitvo
  setParametrization: React.Dispatch<
    React.SetStateAction<ResponseGetParametrizacaoEfeitvo>
  >
  selectedMovement: ISelectedMoviment
  handleError: (_data: any) => void
  handleChangeMobilizacao: (_name: ICheckboxTypes) => void
  dataAtuacaoInvalida: boolean
  setDataAtuacaoInvalida: React.Dispatch<React.SetStateAction<boolean>>
  blockIndividualVision: boolean
  blockTotalVision: boolean
  handleBlock: (
    _value: boolean,
    _type: 'visaoIndividual' | 'visaoTotal'
  ) => void
  isAnalista: boolean
  isContraparte: boolean
  blockVerifyIndividual: boolean
  setBlockVerifyIndividual: React.Dispatch<React.SetStateAction<boolean>>
  setIsContraparte: React.Dispatch<React.SetStateAction<boolean>>
  setIsAnalista: React.Dispatch<React.SetStateAction<boolean>>
  setErrorsTotalVision: React.Dispatch<React.SetStateAction<IErrorsVisionTotal>>
  errorsTotalVision: IErrorsVisionTotal
  resetContext: () => void
}

export default function MovMensalProvider({ children }) {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const { user } = useAuth() //PEGA PERFIL DESSE OBJ

  const [selectedMovement, setSelectedMovement] =
    useState<ISelectedMoviment>(undefined)
  const [isAnalista, setIsAnalista] = useState<boolean>(false)
  const [isContraparte, setIsContraparte] = useState<boolean>(false)
  const [parametrization, setParametrization] =
    useState<ResponseGetParametrizacaoEfeitvo>(DEFAULT_PARAMETRIZACAO)
  const [movState, setMovState] = useState<IVisaoIndividual>(DEFAULT_MOV_STATE)
  const [dataAtuacaoInvalida, setDataAtuacaoInvalida] = useState(false)
  const [blockIndividualVision, setBlockIndividualVision] =
    useState<boolean>(false)
  const [blockTotalVision, setBlockTotalVision] = useState<boolean>(false)
  const [errorsTotalVision, setErrorsTotalVision] =
    useState<IErrorsVisionTotal>(DEFAULT_ERROS_FORM)
  const [blockVerifyIndividual, setBlockVerifyIndividual] =
    useState<boolean>(false)

  const handleError = (error: any) => {
    setLoading('idle')
    showModal({
      hideButtonCancel: true,
      message: error.mensagem || error.message || 'Erro. Tente Novamente.',
      txtButtonConfirm: 'Fechar',
      type: 'error'
    })
  }

  const handleBlock = (
    value: boolean,
    type: 'visaoIndividual' | 'visaoTotal'
  ) => {
    if (type === 'visaoIndividual') {
      setBlockIndividualVision(value)
    }

    if (type === 'visaoTotal') {
      setBlockTotalVision(value)
    }
  }

  const handleChangeMobilizacao = (name: ICheckboxTypes) => {
    if (name === 'flSemMob') {
      setMovState((old) => ({ ...old, flStatusMobilizacao: '0' }))
    }
    if (name === 'flComMob') {
      setMovState((old) => ({ ...old, flStatusMobilizacao: '1' }))
    }
    if (name === 'flCompartilhado') {
      setMovState((old) => ({ ...old, flStatusMobilizacao: '2' }))
    }

    setBlockVerifyIndividual(false)
  }

  const resetContext = () => {
    setMovState(DEFAULT_MOV_STATE)
    setErrorsTotalVision(DEFAULT_ERROS_FORM)
    setSelectedMovement(undefined)
  }

  return (
    <MovMensalContext.Provider
      value={{
        blockIndividualVision,
        blockTotalVision,
        blockVerifyIndividual,
        dataAtuacaoInvalida,
        errorsTotalVision,
        handleBlock,
        handleChangeMobilizacao,
        handleError,
        isAnalista,
        isContraparte,
        movState,
        parametrization,
        resetContext,
        selectedMovement,
        setBlockVerifyIndividual,
        setDataAtuacaoInvalida,
        setErrorsTotalVision,
        setIsAnalista,
        setIsContraparte,
        setMovState,
        setParametrization,
        setSelectedMovement
      }}
    >
      {children}
    </MovMensalContext.Provider>
  )
}

export interface ISelectedMoviment {
  contratoId: number
  cdContratoPrincipal: string
  mesAno: string
  nmEmpresaFornecedor: string
  nmEmpresaFornecedorPrincipal: string
  txObjetoContrato: string
  flCompAtual: boolean
  dtVigenciaInicio: Date
  dtVigenciaFim: Date
  fornecedorId: number
  cdContrato: string
  movimentacaoId?: number
}

export interface IColaborador {
  idMovEfetivo: number
  movimentacaoId: number
  fornecedorId: number
  contratoId: string
  cdContrato: string
  cdNacionalidade: string
  cdCpfPassaporte: string
  cdGenero: string
  nmColaborador: string
  cdStatus: string
  dtAdmissao: string
  dtDemissao: string
  cdTipoEfetivo: string
  txFuncao: string
  nuMesAnoCompetencia: string
  cdCategoria: string
  vlHoraExtras: number
  vlSalBase: number
  flDelete: boolean
}

export interface IVisaoTotal {
  idMovimentacao: number
  contratoCompartilhadoId: number
  qtAtivo: number
  qtDemitido: number
  qtAcesso: number
  qtTransferido: number
  qtAfastado: number
  qtCompartilhado: number
  qtTotalEfetivo: number
  qtClt: number
  qtSocio: number
  qtCooperado: number
  qtEstrangeiro: number
  qtEstagiario: number
  qtAutonomo: number
  qtMei: number
  qtNeAnterior: number
  qtNeAtual: number
  peNePercVarAtual: number
  vlNeVariacaoAtual: number
  txNeJustificativa: string
  qtHeQtd: number | string
  qtHeNormal: number | string
  peHeVariacao: number
  txHeJustificativa: string
  vlSalBaseAnterior: number | string
  vlSalBase: number | string
  flPagtoAnterior: number
  vlFolhaPagto: number | string
  vlHoraExtras: number | string
  txSalBaseJustificativa: string
  flMovValida: boolean
}

export interface IErrorsVisionTotal {
  txQuando: IErroObject | null
  txPorque: IErroObject | null
  vlFolhaPagto: IErroObject | null
  vlHoraExtras: IErroObject | null
  qtHeNormal: IErroObject | null
  qtHeQtd: IErroObject | null
  txSalBaseJustificativa: IErroObject | null
  vlSalBase: IErroObject | null
  txHeJustificativa: IErroObject | null
  txNeJustificativa: IErroObject | null
}

interface IErroObject {
  message: string
}
